/* eslint-disable no-console */
import domtoimage from 'dom-to-image-more';
import jsPDF from 'jspdf';

function dumpCSSText(element: any) {
	let s = '';
	let o = getComputedStyle(element);
	for (let i = 0; i < o.length; i++) {
		s += o[i] + ':' + o.getPropertyValue(o[i]) + ';';
	}
	return s;
}

// Helper function to prepare DOM for capture
async function prepareDOMForCapture(element: HTMLElement) {
	// Get the element's owner document
	const doc = element.ownerDocument;

	// Query all elements with class 'template'
	const templates = element.querySelectorAll(
		'.template'
	) as NodeListOf<HTMLImageElement>;
	if (templates.length > 0) {
		// Create an array of promises to wait for all images to load
		const loadPromises = Array.from(templates).map((template) => {
			const sourceTemplate = template.dataset.source || '';
			template.setAttribute('src', sourceTemplate);
			return new Promise((resolve, reject) => {
				template.onload = resolve;
				template.onerror = reject;
			});
		});
		// Wait for all images to finish loading
		await Promise.all(loadPromises);
	}

	// Ensure all images are fully loaded
	const images = element.querySelectorAll('img');
	if (images.length > 0) {
		const imagePromises = Array.from(images).map((img: any) => {
			if (img.complete) return Promise.resolve();
			return new Promise((resolve) => {
				img.onload = resolve;
				img.onerror = resolve; // Resolve even on error to avoid hanging
			});
		});
		await Promise.all(imagePromises);
	}

	// Remove all unwanted borders
	removeBorders(element);

	// Process input fields
	Array.from(element.getElementsByClassName('deviated-input-v2')).forEach(
		(input: any) => {
			let div = doc.createElement('div');
			div.innerText = input.value;
			div.style.cssText = dumpCSSText(input);
			div.style.border = 'none'; // Remove border
			input.style.display = 'none';
			input.parentElement.append(div);
		}
	);

	// Process textareas
	Array.from(element.querySelectorAll('textarea')).forEach((textArea: any) => {
		const div = doc.createElement('div');
		div.innerText = textArea.value;
		div.className = textArea.classList;
		div.style.cssText = dumpCSSText(textArea);
		div.style.display = 'grid';
		div.style.alignContent = 'center';
		div.style.overflow = 'hidden';
		div.style.wordBreak = 'break-word';
		div.style.paddingBottom = textArea.style.paddingBottom || '14px';
		div.style.paddingLeft = textArea.style.paddingLeft || 'unset';
		div.style.border = 'none'; // Remove border
		textArea.style.display = 'none';
		textArea.parentElement.append(div);
	});

	// Process date inputs
	Array.from(element.getElementsByClassName('date-input')).forEach(
		(input: any) => {
			const antDateInput = input.querySelector('.ant-picker-input')?.firstChild;
			if (!antDateInput) return;
			let dateInput = doc.createElement('div');
			dateInput.innerHTML = antDateInput.value;
			dateInput.style.cssText = dumpCSSText(input);
			dateInput.style.fontFamily = 'inherit';
			dateInput.style.fontSize = input.style?.fontSize || '14px';
			dateInput.style.marginTop = input.style?.marginTop || '-14px';
			dateInput.style.marginLeft = input.style?.marginLeft || '0';
			dateInput.style.width = input.style?.width || 'max-content';
			dateInput.style.backgroundColor = 'transparent';
			dateInput.style.minHeight = '40px';
			dateInput.style.lineHeight = 'normal';
			dateInput.style.border = 'none'; // Remove border
			dateInput.style.whiteSpace = 'pre';
			dateInput.style.letterSpacing = input.style?.letterSpacing || '1';
			input.style.display = 'none';
			input.parentElement.append(dateInput);
		}
	);

	// Process range date inputs
	Array.from(element.getElementsByClassName('range-date-input')).forEach(
		(input: any) => {
			const antDateInputFirst =
				input.querySelectorAll('.ant-picker-input')?.[0]?.firstChild;
			const antDateInputSecond =
				input.querySelectorAll('.ant-picker-input')?.[1]?.firstChild;
			if (!antDateInputFirst) return;
			let dateInput = doc.createElement('input');
			dateInput.value = antDateInputFirst?.value
				? `${antDateInputFirst?.value} ~ ${antDateInputSecond?.value}`
				: '';
			dateInput.style.cssText = dumpCSSText(input);
			dateInput.style.fontFamily = 'inherit';
			dateInput.style.fontSize = input.style?.fontSize || '10px';
			dateInput.style.marginTop = '-20px';
			dateInput.style.backgroundColor = 'transparent';
			dateInput.style.minHeight = '40px';
			dateInput.style.textAlign = input.style.textAlign || 'center';
			dateInput.style.border = 'none'; // Remove border
			input.style.display = 'none';
			input.parentElement.append(dateInput);
		}
	);

	// Process select elements
	Array.from(element.getElementsByTagName('select')).forEach((select: any) => {
		const div = doc.createElement('div');
		div.innerText = select.dataset.value;
		div.className = select?.classList;

		if (select?.classList?.toString()?.includes('select-1')) {
			div.style.width = '47px';
		}
		div.style.fontSize = select?.style?.fontSize || '10px';
		div.style.textAlign = 'center';
		div.style.marginTop = '4px';
		div.style.border = 'none'; // Remove border
		select.style.display = 'none';
		select.parentElement.append(div);
	});

	// Hide unnecessary elements
	Array.from(element.getElementsByClassName('icon-delete-img-form')).forEach(
		(element: any) => {
			element.style.display = 'none';
		}
	);

	element.querySelectorAll('.highlight-box').forEach((element: any) => {
		element.style.display = 'none';
	});

	// Special handling for Ant Design components
	element
		.querySelectorAll('.ant-select-selection-item')
		.forEach((element: any) => {
			if (element && element.parentElement) {
				const textContent = element.textContent || '';
				const div = doc.createElement('div');
				div.innerText = textContent;
				div.style.cssText = element.style.cssText;
				div.style.border = 'none'; // Remove border
				element.parentElement.appendChild(div);
			}
		});

	// Make Ant components display correctly
	element
		.querySelectorAll('.ant-select, .ant-picker, .ant-dropdown')
		.forEach((element: any) => {
			element.style.opacity = '1';
			element.style.visibility = 'visible';
			// Remove border
			element.style.border = 'none';
		});
}

// Function to remove all borders
function removeBorders(element: HTMLElement) {
	// Process the root element's border first
	element.style.border = 'none';
	element.style.borderTop = 'none';
	element.style.borderRight = 'none';
	element.style.borderBottom = 'none';
	element.style.borderLeft = 'none';
	element.style.outline = 'none';
	element.style.boxShadow = 'none';

	// Apply to all parent elements recursively
	let parentElement = element.parentElement;
	while (parentElement && parentElement !== document.body) {
		parentElement.style.border = 'none';
		parentElement.style.borderTop = 'none';
		parentElement.style.borderRight = 'none';
		parentElement.style.borderBottom = 'none';
		parentElement.style.borderLeft = 'none';
		parentElement.style.outline = 'none';
		parentElement.style.boxShadow = 'none';
		parentElement = parentElement.parentElement;
	}

	// Select all elements in the container
	const allElements = element.querySelectorAll('*');

	// Remove borders for all elements
	allElements.forEach((el: any) => {
		// Only apply to elements with borders
		if (
			el.style.border ||
			getComputedStyle(el).border !== 'none' ||
			getComputedStyle(el).borderWidth !== '0px'
		) {
			// Save original padding values
			const originalPadding = {
				top: el.style.paddingTop || getComputedStyle(el).paddingTop,
				right: el.style.paddingRight || getComputedStyle(el).paddingRight,
				bottom: el.style.paddingBottom || getComputedStyle(el).paddingBottom,
				left: el.style.paddingLeft || getComputedStyle(el).paddingLeft,
			};

			// Remove all borders
			el.style.border = 'none';
			el.style.borderTop = 'none';
			el.style.borderRight = 'none';
			el.style.borderBottom = 'none';
			el.style.borderLeft = 'none';
			el.style.outline = 'none';
			el.style.boxShadow = 'none';

			// Restore padding
			el.style.paddingTop = originalPadding.top;
			el.style.paddingRight = originalPadding.right;
			el.style.paddingBottom = originalPadding.bottom;
			el.style.paddingLeft = originalPadding.left;
		}
	});

	// Process elements that might have borders via CSS classes
	const elementsWithBorderClass = element.querySelectorAll(
		'[class*="border"], [class*="outline"], .ant-form-item, .ant-input, .ant-select, .ant-picker, table, tr, td, th, div[class*="table"], div[class*="cell"], div[class*="row"], div[class*="col"]'
	);

	elementsWithBorderClass.forEach((el: any) => {
		el.style.border = 'none';
		el.style.outline = 'none';
		el.style.boxShadow = 'none';
	});

	// Special handling for table elements
	const tableElements = element.querySelectorAll('table, tr, td, th');
	tableElements.forEach((el: any) => {
		el.style.border = 'none';
		el.style.borderCollapse = 'collapse';
		el.removeAttribute('border');
		el.removeAttribute('rules');
		el.removeAttribute('frame');
	});
}

export const printDocument = async (
	pages: Array<HTMLDivElement | null>,
	name: string,
	debug: boolean = false
) => {
	if (debug) console.log('🔍 [PDF Debug] Starting PDF generation for:', name);

	const pdf = new jsPDF('p', 'mm', 'a4');
	const imgWidth = 210;
	const position = 0;

	// Wait to ensure all elements have been rendered
	if (debug) console.log('🔍 [PDF Debug] Waiting for elements to render...');
	await new Promise((resolve) => setTimeout(resolve, 1000));

	// Save initial scroll state
	const originalScrollPosition = {
		x: window.scrollX,
		y: window.scrollY,
	};
	if (debug)
		console.log(
			'🔍 [PDF Debug] Saved scroll position:',
			originalScrollPosition
		);

	if (debug) console.log(`🔍 [PDF Debug] Processing ${pages.length} pages...`);
	const listPages = await Promise.all(
		pages.map(async (page: any, pageIndex: number) => {
			if (page) {
				if (debug)
					console.log(`🔍 [PDF Debug] Processing page ${pageIndex + 1}...`);

				// Store original styles to restore after capture
				const originalStyle = {
					width: page.style.width,
					height: page.style.height,
					overflow: page.style.overflow,
					transform: page.style.transform,
					transformOrigin: page.style.transformOrigin,
					position: page.style.position,
					zIndex: page.style.zIndex,
					margin: page.style.margin,
					padding: page.style.padding,
					backgroundColor: page.style.backgroundColor,
				};
				if (debug)
					console.log(
						`🔍 [PDF Debug] Page ${pageIndex + 1} original styles saved`
					);

				// Temporarily adjust styles for optimal capture
				page.style.overflow = 'visible';
				page.style.position = 'relative';
				page.style.zIndex = '1';
				page.style.backgroundColor = '#FFFFFF';
				if (debug)
					console.log(
						`🔍 [PDF Debug] Page ${pageIndex + 1} styles adjusted for capture`
					);

				// Force layout recalculation
				void page.offsetHeight;

				try {
					if (debug)
						console.log(
							`🔍 [PDF Debug] Cloning DOM for page ${pageIndex + 1}...`
						);
					// Clone DOM to process without affecting the original UI
					const clonedNode = page.cloneNode(true) as HTMLElement;
					const container = document.createElement('div');
					container.style.position = 'absolute';
					container.style.left = '-9999px';
					container.style.top = '-9999px';
					container.appendChild(clonedNode);
					document.body.appendChild(container);
					if (debug)
						console.log(
							`🔍 [PDF Debug] DOM cloned for page ${pageIndex + 1}, dimensions: ${clonedNode.offsetWidth}x${clonedNode.scrollHeight || clonedNode.offsetHeight}`
						);

					// Process elements in the cloned DOM
					if (debug)
						console.log(
							`🔍 [PDF Debug] Preparing DOM for capture on page ${pageIndex + 1}...`
						);
					await prepareDOMForCapture(clonedNode);
					if (debug)
						console.log(
							`🔍 [PDF Debug] DOM preparation complete for page ${pageIndex + 1}`
						);

					// Use dom-to-image-more instead of html2canvas
					if (debug)
						console.log(
							`🔍 [PDF Debug] Converting page ${pageIndex + 1} to image...`
						);
					const dataUrl = await domtoimage.toPng(clonedNode, {
						bgcolor: '#FFFFFF',
						height: clonedNode.scrollHeight || clonedNode.offsetHeight,
						width: clonedNode.offsetWidth,
						scale: 1, // Increase resolution
						quality: 0.8,
						cacheBust: false,
						style: {
							'transform-origin': 'top left',
							transform: 'scale(1)',
						},
					});
					if (debug)
						console.log(
							`🔍 [PDF Debug] Page ${pageIndex + 1} converted to image successfully`
						);

					// Create image from data URL
					const img = new Image();
					const imgPromise = new Promise<HTMLImageElement>((resolve) => {
						img.onload = () => {
							if (debug)
								console.log(
									`🔍 [PDF Debug] Image loaded for page ${pageIndex + 1}, dimensions: ${img.width}x${img.height}`
								);
							resolve(img);
						};
						img.src = dataUrl;
					});
					const loadedImg = await imgPromise;

					// Clean up cloned DOM
					document.body.removeChild(container);
					if (debug)
						console.log(
							`🔍 [PDF Debug] Cleaned up cloned DOM for page ${pageIndex + 1}`
						);

					// Restore original styles for the page
					Object.assign(page.style, originalStyle);
					if (debug)
						console.log(
							`🔍 [PDF Debug] Restored original styles for page ${pageIndex + 1}`
						);

					return loadedImg;
				} catch (error) {
					console.error('Error capturing page:', error);
					if (debug)
						console.log(
							`🔍 [PDF Debug] Error processing page ${pageIndex + 1}:`,
							error
						);
					// Restore original styles for the page
					Object.assign(page.style, originalStyle);
					throw error;
				}
			}
			if (debug && !page)
				console.log(
					`🔍 [PDF Debug] Page ${pageIndex + 1} is null or undefined, skipping`
				);
			return null;
		})
	);

	// Restore scroll position
	window.scrollTo(originalScrollPosition.x, originalScrollPosition.y);
	if (debug) console.log('🔍 [PDF Debug] Restored original scroll position');

	// Create PDF from images
	if (debug)
		console.log(
			`🔍 [PDF Debug] Creating PDF with ${listPages.filter(Boolean).length} valid pages`
		);
	listPages.forEach((img, index) => {
		if (img) {
			let imgHeight = (img.height * imgWidth) / img.width;
			if (debug)
				console.log(
					`🔍 [PDF Debug] Adding page ${index + 1} to PDF, dimensions: ${imgWidth}mm x ${imgHeight}mm`
				);

			pdf.addImage(
				img.src,
				'PNG',
				0,
				position,
				imgWidth,
				imgHeight,
				'',
				'FAST'
			);

			if (index < listPages.length - 1) {
				pdf.addPage();
				if (debug) console.log(`🔍 [PDF Debug] Added new page to PDF document`);
			}
		} else if (debug) {
			console.log(
				`🔍 [PDF Debug] Skipping page ${index + 1} (no image available)`
			);
		}
	});

	if (debug)
		console.log('🔍 [PDF Debug] PDF generation complete, creating file...');
	const pdfFile = new File([pdf.output('blob')], name, {
		type: 'application/pdf',
	});
	if (debug)
		console.log(
			`🔍 [PDF Debug] PDF file "${name}" created successfully, size: ${(pdfFile.size / 1024).toFixed(2)}KB`
		);

	return pdfFile;
};

export const printDocumentMobile = async (
	pages: Array<HTMLDivElement | null>,
	name: string,
	debug: boolean = false
) => {
	const pdf = new jsPDF('p', 'mm', 'a4');
	const imgWidth = 210;
	const position = 0;

	// Save initial scroll state
	const originalScrollPosition = {
		x: window.scrollX,
		y: window.scrollY,
	};

	const listPages = await Promise.all(
		pages.map(async (page: any) => {
			if (page) {
				// Store original styles to restore after capture
				const originalStyle = {
					width: page.style.width,
					height: page.style.height,
					overflow: page.style.overflow,
					transform: page.style.transform,
					transformOrigin: page.style.transformOrigin,
					position: page.style.position,
					zIndex: page.style.zIndex,
					margin: page.style.margin,
					padding: page.style.padding,
					backgroundColor: page.style.backgroundColor,
				};

				// Temporarily adjust styles for optimal capture
				page.style.overflow = 'visible';
				page.style.position = 'relative';
				page.style.zIndex = '1';
				page.style.backgroundColor = '#FFFFFF';

				// Force layout recalculation
				void page.offsetHeight;

				try {
					// Clone DOM to process without affecting the original UI
					const clonedNode = page.cloneNode(true) as HTMLElement;
					const container = document.createElement('div');
					container.style.position = 'absolute';
					container.style.left = '-9999px';
					container.style.top = '-9999px';
					container.appendChild(clonedNode);
					document.body.appendChild(container);

					// Process elements in the cloned DOM
					await prepareDOMForCapture(clonedNode);

					// Use dom-to-image-more instead of html2canvas
					const dataUrl = await domtoimage.toPng(clonedNode, {
						bgcolor: '#FFFFFF',
						height: clonedNode.scrollHeight || clonedNode.offsetHeight,
						width: clonedNode.offsetWidth,
						scale: 2, // Increase resolution
						quality: 1,
						style: {
							'transform-origin': 'top left',
							transform: 'scale(1)',
						},
					});

					// Create image from data URL
					const img = new Image();
					const imgPromise = new Promise<HTMLImageElement>((resolve) => {
						img.onload = () => resolve(img);
						img.src = dataUrl;
					});
					const loadedImg = await imgPromise;

					// Clean up cloned DOM
					document.body.removeChild(container);

					// Restore original styles for the page
					Object.assign(page.style, originalStyle);

					return loadedImg;
				} catch (error) {
					console.error('Error capturing page:', error);
					// Restore original styles for the page
					Object.assign(page.style, originalStyle);
					throw error;
				}
			}
		})
	);

	// Restore scroll position
	window.scrollTo(originalScrollPosition.x, originalScrollPosition.y);

	// Create PDF from images
	listPages.forEach((img, index) => {
		if (img) {
			let imgHeight = (img.height * imgWidth) / img.width;
			pdf.addImage(
				img.src,
				'PNG',
				0,
				position,
				imgWidth,
				imgHeight,
				'',
				'FAST'
			);

			if (index < listPages.length - 1) {
				pdf.addPage();
			}
		}
	});

	return new File([pdf.output('blob')], name, { type: 'application/pdf' });
};

export const handlePrint = async ({
	pageRefs,
	printIframeRef,
	printUrlRef,
	fileName = '',
}: {
	pageRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
	printIframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
	printUrlRef: React.MutableRefObject<string | null>;
	fileName?: string;
}) => {
	try {
		if (printIframeRef.current) {
			document.body.removeChild(printIframeRef.current);
			printIframeRef.current = null;
		}
		if (printUrlRef.current) {
			URL.revokeObjectURL(printUrlRef.current);
			printUrlRef.current = null;
		}

		const filePdf = await printDocument(pageRefs.current, fileName);

		const blob = new Blob([filePdf], { type: 'application/pdf' });
		const url = URL.createObjectURL(blob);

		// Create hidden iframe to print PDF
		const iframe = document.createElement('iframe');
		iframe.style.display = 'none';
		iframe.src = url;
		document.body.appendChild(iframe);

		// Store references
		printIframeRef.current = iframe;
		printUrlRef.current = url;

		iframe.onload = () => {
			try {
				// Try to print from iframe
				iframe.contentWindow?.print();
			} catch (error) {
				// Fallback: open PDF in new tab
				window.open(url, '_blank');
			}
		};
	} catch (error) {
		console.error('Error printing PDF:', error);
	}
};
